import { globals } from 'config/globals';
import { HomeField } from 'app/views/home/webgl/particles/fields/HomeField';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import HomeFieldScreensPixelDataConfig from 'app/views/home/webgl/particles/config/HomeFieldScreensPixelDataConfig';
import { particles } from 'app/contracts/Particles';
import { geometry } from 'app/contracts/Geometry';
import { ParticleLayoutType, RepellType } from 'app/enum/Particles';
import Config from 'config/settings';
import DelayHelper from 'utils/timers';
import HomeMicroShapeController from 'app/views/home/webgl/particles/scenes/HomeMicroShapeController';
import { RadialHalftoneImage } from 'app/views/home/webgl/particles/scenes/RadialHalftoneImage';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import HomeColours from 'app/views/home/config/HomeColours';
import { Inertia } from 'utils/motion/Inertia';
import ComboCounter from 'utils/ComboCounter';
import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';
import ParticleBehaviourLookup from 'particles/ParticleBehaviourLookup';
import HalftonePersonPixelCache from 'app/data/HomePixelCache/HalftonePersonPixelCache';
import { ImagePixelDataGrid } from 'utils/ImagePixelDataGrid';
import { Simple } from 'utils/analytics';
import BaseDepartmentScene from 'app/views/home/webgl/particles/departments/BaseDepartmentScene';
import { device } from 'utils/device';
type HomeMicroShapeControllers = { [key: string]: HomeMicroShapeController };

export default class ScreensDepartment extends BaseDepartmentScene {
	private _screensPool: Array<HomeParticle>;
	private _shapeParticles: Array<HomeParticle>;
	private _screensItemsIndex: number = 0;
	private _active: boolean = false;
	private _shapeFrameCounter: number = 0;
	private _switchShapeAtFrameCount: number = 30;
	private _frameBG: PIXI.Sprite;

	private _reduceFirstPass = false;
	private _resizeTimeout: number;

	private _containerOffsets = {
		rotation: 0,
		x: 0,
		y: 0,
		xI: new Inertia(5),
		yI: new Inertia(5)
	};

	private _screensParticleContainer: PIXI.Sprite;

	private _radialHalftoneImage: RadialHalftoneImage;

	private _microShapesActive: boolean = false;
	private _microShapes: HomeMicroShapeControllers = {
		topLeft: null,
		topRight: null,
		bottomLeft: null,
		bottomRight: null
	};

	private _microShowOrder = ['topRight', 'bottomLeft', 'topLeft', 'bottomRight'];

	private _sectionsOrder = ['radialHalftoneImage', 'dropMachine', 'swirl'];

	private _comboCounter: ComboCounter;

	constructor(
		private _homeField: HomeField,
		private _sceneProps: particles.ParticlesSceneProps
	) {
		super();
		globals.screensMobileLayout = false;
		if (window.innerWidth < Config.mobileBreakpoint) {
			globals.screensMobileLayout = true;
		}

		this._screensPool = HomeFieldConfig.getParticlePool(this._homeField.particles as Array<HomeParticle>, 'screens');

		this._screensParticleContainer = this._homeField.getSubContainer('screens');

		this._comboCounter = new ComboCounter();

		var gr = new PIXI.Graphics();
		gr.beginFill(0xffffff);
		gr.lineStyle(0);
		gr.drawRect(0, 0, 100, 100);
		gr.endFill();

		this._frameBG = new PIXI.Sprite();
	}

	//Pixel data 0-1 grid of pixel cords that can be translated to
	public set pixelData(data: any) {
	}

	public tick() {
		if (!this._active) return;

		//Align the text with edge of screen

		this._positionParticleContainer();
		this._counter();
		this._comboCounter.tick();

		//Glue the stage's screens-mask-image sprite to the timeline's frame
		//sprite (globals.framesprite crosses the seam — the timeline writes it
		//a beat after create(), hence the guard):
		const ttt = this._homeField.stage.ttt;

		if (globals.framesprite) {
			ttt.x = globals.framesprite.x + 3;
			ttt.y = globals.framesprite.y;
			ttt.scale.y = globals.framesprite.scale.y;
			ttt.scale.x = globals.framesprite.scale.x;
		}

		if (this._radialHalftoneImage) {
			this._radialHalftoneImage.tick();
		}

		for (let i = 0; i < this._screensPool.length; i++) {
			if (this._screensPool[i]._active) {
				this._screensPool[i].screens.testVisible();
			}
		}

		if (this._microShapesActive) {
			for (let k in this._microShapes) {
				this._microShapes[k].tick();
			}
		}
	}

	public startAsActiveScene() {
		this._active = true;
		this._reduceFirstPass = true;
		this._startState();
		gsap.TweenMax.to(this._frameBG, 1, { pixi: { alpha: 0.6 }, ease: Config.linearEase });

		ParticleBehaviourLookup.pause();

		setTimeout(() => {
			ParticleBehaviourLookup.unpause();
		}, 170);

		setTimeout(() => {
			this._screensParticleContainer.visible = true;
		}, 210);
	}

	public stopAsActiveScene() {
		DelayHelper.clearGroup('screens');
		ParticleHitTests.active = false;

		HomeScreens.reset();
		this._active = false;
		this._comboCounter.clear();

		this._screensPool.forEach((particle) => {
			particle.deactivateAllBehaviours(false);
			particle.deactivate();
			particle.active = false;
		});

		this._screensParticleContainer.visible = false;

		this._shapeParticles = [];
	}

	public fromWorkToHome() {
		this._enableForHome();

		gsap.TweenMax.to(this._screensParticleContainer, Config.toWorkTime, {
			pixi: { y: 0 },
			ease: Config.toWorkEase,
			onComplete: () => {}
		});
	}

	public resize() {
		this.stopAsActiveScene();

		if (this._resizeTimeout) {
			clearTimeout(this._resizeTimeout);
		}

		this._resizeTimeout = setTimeout(() => {
			this.startAsActiveScene();
			this._resizeTimeout = null;
		}, 500);

		return;

		this._shapeFrameCounter = 0;

		DelayHelper.setTimeout(
			() => {
				this._nextScreensSection();
				this._shapeFrameCounter = 0;
			},
			50,
			null,
			'screens'
		);
	}

	//Deeplink to work:
	public setupForWork() {
		this._active = false;

		DelayHelper.clearGroup('screens');
		this._comboCounter.clear();
	}

	public showFromWork() {
		this._active = true;
		this._screensParticleContainer.visible = false;

		this.stopAsActiveScene();
		globals.skipNormalParticleUpdates = true;

		DelayHelper.setTimeout(() => {
			globals.skipNormalParticleUpdates = true;
			this.startAsActiveScene();
		}, 20);

		return;
		this._comboCounter.add(() => {}, 1000);
	}

	//PRIVATE
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------

	private _positionParticleContainer() {
		let x =
			this._sceneProps.progress.containerX +
			this._sceneProps.zonePositions.screensParticles.cx * this._sceneProps.sizes.container.w +
			this._containerOffsets.x;

		this._screensParticleContainer.x = x;
		this._screensParticleContainer.y = this._containerOffsets.y;

		this._screensParticleContainer.rotation = this._containerOffsets.rotation;

		this._sceneProps.mouse.particleContainersX.screens = x;
		this._sceneProps.mouse.particleContainersY.screens = this._screensParticleContainer.y;
	}

	private _counter(): void {
		this._shapeFrameCounter += (window as any).deltaTime;
		if (this._shapeFrameCounter > this._switchShapeAtFrameCount) {
			this._shapeFrameCounter = 0;
			this._nextScreensSection();

			return;
		}
	}

	/** Set the start state when the scene becomes active: **/
	private _startState(): void {
		this._screensItemsIndex = 0;
		this._shapeFrameCounter = 0;
		this._setParticlesAtStart();

		DelayHelper.setTimeout(() => {
			this._nextScreensSection();
		}, 40);
	}

	private _totalPasses = 0;
	private _nextScreensSection() {
		this._microShapesActive = false;
		this._radialHalftoneImage = null;
		ParticleHitTests.active = false;

		this._containerOffsets.x = this._containerOffsets.xI.value = this._containerOffsets.xI.target = 0;
		this._containerOffsets.y = this._containerOffsets.yI.value = this._containerOffsets.yI.target = 0;
		this._containerOffsets.rotation = 0;

		if (this._screensItemsIndex == 0 && !this._reduceFirstPass) {
			this._totalPasses++;
			Simple.event('home_screens_full_phase_complete', { total_passes: this._totalPasses });
		}

		Simple.event('home_screens_next_phase', { phase: this._sectionsOrder[this._screensItemsIndex] });

		if (this._sectionsOrder[this._screensItemsIndex] == 'whoozyImage') {
			this._doWhoozyImage();
			this._switchShapeAtFrameCount = this._roughFrameSecs(10);
		} else if (this._sectionsOrder[this._screensItemsIndex] == 'swirl') {
			this._doSwirl();
			this._switchShapeAtFrameCount = this._roughFrameSecs(7);
		} else if (this._sectionsOrder[this._screensItemsIndex] == 'microShapes') {
			this._doMicroShapes();

			this._switchShapeAtFrameCount = this._roughFrameSecs(11);
		} else if (this._sectionsOrder[this._screensItemsIndex] == 'radialHalftoneImage') {
			this._doRadialHalftoneImage();
			this._switchShapeAtFrameCount = this._roughFrameSecs(9);
		} else if (this._sectionsOrder[this._screensItemsIndex] == 'dropMachine') {
			this._doDropMachine();
			this._switchShapeAtFrameCount = this._roughFrameSecs(8.8);
		} else if (this._sectionsOrder[this._screensItemsIndex] == 'pixelElephant') {
			this._pixelElephant();
			this._switchShapeAtFrameCount = this._roughFrameSecs(9.8);
		} else if (this._sectionsOrder[this._screensItemsIndex] == 'ttBallon') {
			this._ttBallon();
			this._switchShapeAtFrameCount = this._roughFrameSecs(8);
		}

		if (this._reduceFirstPass) {
			this._switchShapeAtFrameCount *= 0.55;
			this._reduceFirstPass = false;
		}

		this._screensItemsIndex++;
		if (this._screensItemsIndex >= this._sectionsOrder.length) {
			this._screensItemsIndex = 0;
		}
	}

	private _roughFrameSecs(secs) {
		return Math.round(secs * 60);
	}



	private _setParticlesAtStart() {
		this._shapeParticles = this._screensPool;

		let share = Math.round(this._shapeParticles.length / 4);
		let p1 = this._shapeParticles.slice(0, share);
		let p2 = this._shapeParticles.slice(share);

		p1.forEach(this._setParticleAtStart.bind(this));

		DelayHelper.setTimeout(() => {
			p2.forEach(this._setParticleAtStart.bind(this));
		}, 20);
	}

	private _setParticleAtStart(particle) {
		particle.deactivateAllBehaviours();
		particle.screens.initScreensShape(true, false);
		particle.activate();
	}


	/** Burst transition between layouts */
	private _repositionGravityBurst(
		type: ParticleLayoutType,
		position: geometry.Vector2,
		size: geometry.Size,
		config: any = null,
		particles: Array<particles.Particle> = null,
		randomSeed: number = 0,
		instant: boolean = false,
		frame = null
	): void {
		let layout: particles.ParticleLayout = this._sceneProps.layouts[type];

		//Get an array of positions to set the partciles to:
		let positions: Array<particles.ParticleLookupProps> = layout.get(
			this._shapeParticles.length,
			position,
			size,
			config
		);

		let burstType = Math.ceil(Math.random() * 12.999);
		let bigRandom = Math.random() < 0.5;
		let subType;

		this._shapeParticles.forEach((particle, index: number) => {
			if (burstType > 6) {
				subType = Math.ceil(Math.random() * 4.999);
			}

			particle.activate();
			particle.screens.initScreensShape(true, false);

			if (config.pixelData[index]) {
				(particle as HomeParticle).screens.gravityBurst(
					positions[index],
					config.pixelData[index],
					burstType,
					bigRandom,
					frame,
					subType
				);
			}
		});
	}


	private _pixelElephant() {
		let sizes = {
			topLeft: { x: -0.0905, y: 0.165, w: 0.077, h: 0.3 },
			topRight: { x: -0.0032, y: 0.225, w: 0.05, h: 0.2 },
			bottomLeft: { x: -0.1116, y: 0.515, w: 0.05, h: 0.2 },
			bottomRight: { x: -0.057, y: 0.518, w: 0.05, h: 0.2 }
		};

		let item = HomeFieldScreensPixelDataConfig.dataSet.m4g;

		let i = 0;
		this._shapeParticles.forEach((particle) => {
			particle.repell.active = true;
			particle.particlePhysics.active = false;
			particle.repell.type = RepellType.PUSH;
			//Allow for 150px circle and very tight ring:
			particle.repell.radius = 100;
			particle.repell.randomisePower(30, 50);

			particle.animatedProps._x.setVectorConfig(true);
			particle.animatedProps._y.setVectorConfig(true);

			if (item && item.pixelData[i]) {
				particle.activate();

				particle.colour.set(item.pixelData[i].rgba);

				let scaleSeed = Math.random() > 0.2 ? 0.15 : 0.6;
				particle.animatedProps.scale = particle.animatedProps.targetScale = 0.1 + Math.random() * scaleSeed;

				let baseScale = 0.1 + Math.random() * scaleSeed;

				particle.animatedProps.startBreathing(baseScale, 0.005 + Math.random() * 0.01, Math.random() * 0.8);

				particle.mouseParallax.active = false;
				particle.animatedProps.alpha = particle.animatedProps.targetAlpha = 1;

				let indentX = 0.013;
				let indentY = 0.02;
				let wCorrect = 0.7;
				let yCorrect = 0.8;
				let x = sizes.topLeft.x + indentX + item.pixelData[i].x * (sizes.topLeft.w * wCorrect);
				let y = sizes.topLeft.y + indentY + item.pixelData[i].y * (sizes.topLeft.h * yCorrect);

				particle.position.set({ x: x, y: y - 0.3 }, true);
				particle.position.set({ x: x, y: y });

				this._comboCounter.add(() => {
					let xD = Math.random() < 0.5 ? -1 : 1;
					let yD = Math.random() < 0.5 ? -1 : 1;
					particle.position.set({ x: x + xD * (Math.random() * 0.08), y: y + yD * (Math.random() * 0.1) });
				}, 5500);

				this._comboCounter.add(
					() => {
						particle.animatedProps.targetAlpha = 0;
						particle.animatedProps.targetScale = particle.animatedProps.targetScaleSaved = 0;
						particle.animatedProps.stopBreathing();

						particle.animatedProps.setEase({
							alpha: 30,
							scale: 30
						});
					},
					8000 + Math.random() * 600
				);
			} else {
				particle.hide();
			}

			i++;
		});

		HomeScreens.on('topLeft');

		this._comboCounter.clear();

		this._comboCounter.add(() => HomeScreens.on('topRight'), 6200);

		this._comboCounter.add(() => {
			HomeScreens.off('topLeft');
			HomeScreens.off('topRight');
		}, 9000);
	}

	private _ttBallon() {
		this._comboCounter.clear();

		let sizes = {
			topLeft: { x: -0.0905, y: 0.165, w: 0.077, h: 0.3 },
			topRight: { x: -0.0032, y: 0.225, w: 0.05, h: 0.2 },
			bottomLeft: { x: -0.1116, y: 0.515, w: 0.05, h: 0.2 },
			bottomRight: { x: -0.057, y: 0.518, w: 0.05, h: 0.2 }
		};

		let item = HomeFieldScreensPixelDataConfig.dataSet.ttballon;

		gsap.TweenMax.set(this._containerOffsets, { x: -370, y: -400 });
		gsap.TweenMax.to(this._containerOffsets, 5, { x: -70, y: -300, ease: gsap.Back.easeOut, rotation: 0.05 });
		gsap.TweenMax.to(this._containerOffsets, 5, { x: -50, y: -300, delay: 5, ease: gsap.Back.easeOut, rotation: 0.05 });

		this._containerOffsets.x = this._containerOffsets.xI.value = this._containerOffsets.xI.target = -370;
		this._containerOffsets.y = this._containerOffsets.yI.value = this._containerOffsets.yI.target = -400;

		let i = 0;
		this._shapeParticles.forEach((particle) => {
			particle.repell.active = true;
			particle.particlePhysics.active = false;
			particle.repell.type = RepellType.PUSH;
			//Allow for 150px circle and very tight ring:
			particle.repell.radius = 100;
			particle.repell.randomisePower(30, 50);

			particle.animatedProps.stopBreathing();
			particle.animatedProps._x.setVectorConfig(false);
			particle.animatedProps._y.setVectorConfig(false);

			if (item && item.pixelData[i]) {
				particle.activate();

				particle.colour.set(item.pixelData[i].rgba);
				particle.particleProps.sprite.blendMode = null;

				let scaleSeed = 0.5 * item.pixelData[i].gray;
				particle.animatedProps.scale = particle.animatedProps.targetScale = 0.1 + Math.random() * scaleSeed;
				particle.animatedProps.scale =
					particle.animatedProps.targetScale =
					particle.animatedProps.targetScaleSaved =
						0.25;
				particle.animatedProps.scale = 0;
				particle.animatedProps.alpha = 0;

				let baseScale = 0.1 + Math.random() * scaleSeed;

				if (Math.random() < 0.1) {
					particle.animatedProps.startBreathing(baseScale, 0.001 + Math.random() * 0.04, Math.random() * 0.6);
				}

				particle.mouseParallax.active = false;
				particle.animatedProps.alpha = particle.animatedProps.targetAlpha = 0.8 + Math.random() * 0.2;

				let indentX = -0.01;
				let indentY = 0;
				let wCorrect = 0.8;
				let yCorrect = 1.9;
				let x = sizes.bottomRight.x + indentX + item.pixelData[i].x * (sizes.topLeft.w * wCorrect);
				let y = sizes.bottomRight.y + indentY + item.pixelData[i].y * (sizes.topLeft.h * yCorrect);

				particle.position.set({ x: x, y: y }, true);

				particle.animatedProps.setEase({
					alpha: 30,
					scale: 30
				});

				particle.animatedProps.alpha = particle.animatedProps.targetAlpha = 0;
				particle.animatedProps.hardSetScale(0);

				this._comboCounter.add(() => {
					particle.animatedProps.targetAlpha = 0.8 + Math.random() * 0.2;
					particle.animatedProps.targetScale = particle.animatedProps.targetScaleSaved = 0.1 + Math.random() * 0.25;

					particle.animatedProps._x.setVectorConfig(true);
					particle.animatedProps._y.setVectorConfig(true);
				}, Math.random() * 1000);

				this._comboCounter.add(
					() => {
						let xD = Math.random() < 0.5 ? -1 : 1;
						let yD = Math.random() < 0.5 ? -1 : 1;
						particle.position.set({ x: x + xD * (Math.random() * 0.08), y: y + yD * (Math.random() * 0.1) });

						particle.animatedProps.targetAlpha = 0;
						particle.animatedProps.targetScale = particle.animatedProps.targetScaleSaved = 0;

						particle.animatedProps.setEase({
							alpha: 30,
							scale: 30
						});

						particle.animatedProps._x.setVectorConfig(true);
						particle.animatedProps._y.setVectorConfig(true);
					},
					6000 + Math.random() * 600
				);
			} else {
				particle.hide();
				particle.particleProps.sprite.visible = false;
			}

			i++;
		});

		HomeScreens.on('bottomRight');
		HomeScreens.on('topLeft');

		this._comboCounter.add(() => {
			HomeScreens.on('bottomLeft');
		}, 0);

		this._comboCounter.add(() => {
			HomeScreens.off('bottomLeft');
		}, 1000);

		this._comboCounter.add(() => {
			HomeScreens.off('topLeft');
		}, 7000);

		this._comboCounter.add(() => {
			HomeScreens.off('bottomRight');
		}, 7200);
	}

	private _doDropMachine(): void {
		let totalParticles = HomeFieldConfig.screensDropMachineParticles;
		ParticleHitTests.active = true;

		//On mobile reduce particle count:
		if (device.innerWidth < 700) {
			totalParticles *= HomeFieldConfig.screensDropMachineParticlesMobileScale;
		}

		this._doDrop(totalParticles);
		HomeScreens.showDropMachineBlocks();

		this._comboCounter.clear();

		HomeScreens.allOff();

		let showBottomRightDesktop = 2000;
		let showBottomRightMobile = 1600;
		let showBottomLeft = 1800;
		let hideTopLeftMobile = 6200;
		let hideTopLeftDesktop = 7000;
		let hideTopRightDesktop = 6800;

		//Onm desktop top right is straight away
		this._comboCounter.add(() => {
			HomeScreens.on('topRight');
		}, 500);

		//On mobile top right is shifted to bottom so
		this._comboCounter.add(() => {}, showBottomRightMobile);

		this._comboCounter.add(() => {
			HomeScreens.on('topLeft');
		}, 400);

		this._comboCounter.add(() => {
			HomeScreens.on('bottomLeft');
		}, showBottomLeft);

		//Only show portrait screen on desktop
		this._comboCounter.add(() => {
			HomeScreens.on('bottomRight');
		}, showBottomRightDesktop);

		this._comboCounter.add(() => HomeScreens.hideDropMachineBlocks(), 6000);

		this._comboCounter.add(() => {
			HomeScreens.off('topRight');
		}, hideTopRightDesktop);

		this._comboCounter.add(() => {
			HomeScreens.off('topLeft');
		}, hideTopLeftDesktop);

		this._comboCounter.add(() => {}, hideTopLeftMobile);

		this._comboCounter.add(() => HomeScreens.allOff(), 8200);
	}

	private _doDrop(totalParticles): void {
		if (globals.colourPhase === 'yellow') globals.bounceColourTarget = HomeColours.dynamicSets.m4g;
		else globals.bounceColourTarget = HomeColours.purpleBlue;

		let i = 0;
		let mobileLayout = device.innerWidth <= HomeScreens.switchScreensLayoutAt;

		this._shapeParticles.forEach((particle) => {
			i++;
			if (i < totalParticles) {
				particle.screens.leftDrop();
				particle.particleProps.sprite.blendMode = null;

				let partileTexture = Math.random() < 0.2 ? 'solid-ring.png' : 'solid2.png';
				particle.changeParticle(partileTexture);

				let baseScale;
				let baseSeedBig = mobileLayout ? 0.6 : 0.8;
				let baseSeedSmall = mobileLayout ? 0.38 : 0.5;
				if (Math.random() < 0.2) {
					baseScale = 0.1 + Math.random() * baseSeedBig;
				} else {
					baseScale = 0.1 + Math.random() * baseSeedSmall;
				}

				particle.animatedProps.hardSetScale(baseScale);
				particle.animatedProps.startBreathing(baseScale, Math.random() * 0.02, Math.random() * 0.4);

				let x = -0.08 + Math.random() * 0.15;
				let y = mobileLayout ? Math.random() * -0.15 : Math.random() * 0.15;

				particle.animatedProps.targetAlpha = 0.6 + Math.random() * 0.4;
				particle.animatedProps.setVectorStyleMovement(false);
				particle.position.set({ x: x, y: y }, true);
			} else {
				particle.deactivate();
			}
		});
	}

	private _doRadialHalftoneImage(): void {
		globals.colourPhase = globals.colourPhase === 'yellow' ? 'green' : 'yellow';

		globals.screensZoneScale = this._sceneProps.zones.screens.scale;

		this._radialHalftoneImage = new RadialHalftoneImage(this._sceneProps);

		let totalParticles = HomeFieldConfig.screensHalftoneParticles;
		if (device.innerWidth < 700) {
			totalParticles *= HomeFieldConfig.screensHalftoneParticlesMobileScale;
		}

		this._radialHalftoneImage.setPool(this._shapeParticles.slice(0, totalParticles));

		const nonActive = this._shapeParticles.slice(totalParticles);

		nonActive.forEach((p) => {
			p.deactivate();
		});

		//Home

		let imageGrid = new ImagePixelDataGrid();
		imageGrid.setDataFromCache(HalftonePersonPixelCache.data);
		this._radialHalftoneImage.setImgData(imageGrid);
		this._radialHalftoneImage.offsetX = -0.045;
		this._radialHalftoneImage.offsetY = 0.3;

		HomeScreens.sceneProps = this._sceneProps;
		const relativeSizes = HomeScreens.getTotalRelativeSizes();

		/**
		 * Desktop oreginal sizing:
		 */
		this._radialHalftoneImage.create(0.055, 0.055, 0.0035);
		this._radialHalftoneImage.create(relativeSizes.w / 15, relativeSizes.w / 15, 0.0035);

		//Mobile sizing:

		//Phase in the screens:

		let reduce = this._reduceFirstPass ? 0.55 : 1;

		this._comboCounter.clear();
		this._comboCounter.add(() => this._radialHalftoneImage.offY(), 6000 * reduce);
		this._comboCounter.add(() => {
			HomeScreens.off('bottomRight');
			HomeScreens.off('bottomLeft');
		}, 7000 * reduce);
		this._comboCounter.add(() => HomeScreens.off('topRight'), 8000 * reduce);
		this._comboCounter.add(() => HomeScreens.off('topLeft'), 8200 * reduce);

		//Turn on all screens:
		HomeScreens.allOn(100);
	}

	private _doMicroShapes(): void {
		HomeScreens.sceneProps = this._sceneProps;

		let sizes = {
			topLeft: {
				startX: HomeScreens.getScreenXStartRel('topLeft'),
				startY: HomeScreens.getScreenYStartRel('topLeft'),
				createShapeRows: 8,
				maxBlockCols: 15,
				gridSize: HomeScreens.getSpacingFromWidth('topLeft', 15),
				scale: 0.4 * this._sceneProps.zones.screens.scale
			},
			topRight: {
				startX: HomeScreens.getScreenXStartRel('topRight'),
				startY: HomeScreens.getScreenYStartRel('topRight'),
				createShapeRows: 8,
				maxBlockCols: 12,
				gridSize: HomeScreens.getSpacingFromWidth('topRight', 13),
				scale: 0.18 * this._sceneProps.zones.screens.scale
			},
			bottomLeft: {
				startX: HomeScreens.getScreenXStartRel('bottomLeft'),
				startY: HomeScreens.getScreenYStartRel('bottomLeft'),
				createShapeRows: 7,
				maxBlockCols: 13,
				gridSize: HomeScreens.getSpacingFromWidth('bottomLeft', 13),
				scale: 0.2 * this._sceneProps.zones.screens.scale
			},
			bottomRight: {
				startX: HomeScreens.getScreenXStartRel('bottomRight'),
				startY: HomeScreens.getScreenYStartRel('bottomRight'),
				createShapeRows: 14,
				maxBlockCols: 12,
				gridSize: HomeScreens.getSpacingFromWidth('bottomRight', 12),
				scale: 0.28 * this._sceneProps.zones.screens.scale
			}
		};

		let index = 0;
		let delay = 0;

		let k: keyof HomeMicroShapeControllers;

		this._shapeParticles.forEach((particle) => {
			particle.hide();
		});

		HomeScreens.setAmbientColours(HomeColours.dynamicSets.tt);

		for (let i in this._microShowOrder) {
			k = this._microShowOrder[i];
			let shape = new HomeMicroShapeController(this._sceneProps);

			index = shape.create(this._shapeParticles, index, k, sizes[k]);

			shape.phaseIn(delay, 700);
			shape.juggleSingleBlocks();

			delay += 700;
			this._microShapes[k] = shape;
		}

		this._comboCounter.clear();

		this._comboCounter.add(() => HomeScreens.on('topRight'), 20);
		this._comboCounter.add(() => HomeScreens.on('bottomLeft'), 700);
		this._comboCounter.add(() => HomeScreens.on('topLeft'), 1400);
		this._comboCounter.add(() => HomeScreens.on('bottomRight'), 2100);

		this._comboCounter.add(() => HomeScreens.off('bottomLeft'), 10150);
		this._comboCounter.add(() => HomeScreens.off('topLeft'), 10300);
		this._comboCounter.add(() => HomeScreens.off('topRight'), 10000);
		this._comboCounter.add(() => HomeScreens.off('bottomRight'), 10450);

		this._comboCounter.add(() => {
			for (let j in this._microShapes) {
				this._microShapes[j].reset();
			}
		}, 10500);

		this._microShapesActive = true;
	}

	private _doWhoozyImage() {
		let item = HomeFieldScreensPixelDataConfig.getList()[1];

		let zone = this._sceneProps.zones.screens;
		let size: geometry.Size = {
			w: (item.size.w / this._sceneProps.sizes.container.w) * zone.scale,
			h: (item.size.h / device.innerHeight) * zone.scale
		};

		size = {
			w: 0.04 * zone.scale,
			h: 0.24 * zone.scale
		};

		let position: geometry.Vector2 = {
			x: 0 - size.w / 2,
			y: zone.cy - (size.h / 2) * 1.5
		};

		position = {
			x: -0.024,
			y: 0.05
		};

		//Set frame height.
		//Note do not have to set scale here as this is done at path level on scaling effects
		let ytweak = item.frame.tweakY ? -80 : 60;
		let xTweak = item.frame.tweakX ? -80 : 60;
		let frameHeightPx = item.size.h + ytweak;
		let scaledFrameHeight = frameHeightPx * zone.scale;
		let borderScaled = ytweak * zone.scale;
		let frameCorrected = scaledFrameHeight - borderScaled;
		let correctY = frameCorrected / device.innerHeight / 2;

		let w = item.size.w + xTweak;
		let frame = {
			w: w,
			wr: this._homeField.getRelativeWidth(w),
			h: frameHeightPx,
			hr: this._homeField.getRelativeHeight(frameHeightPx),
			x: zone.cx,
			y: position.y + correctY
		};

		let resetAtStart = true;

		this._repositionGravityBurst(
			ParticleLayoutType.IMAGE_PIXEL_DATA,
			position,
			size,
			{ pixelData: item.pixelData },
			this._shapeParticles,
			700,
			resetAtStart,
			frame
		);

		HomeScreens.allOff();

		this._comboCounter.clear();
		this._comboCounter.add(() => HomeScreens.on('topLeft'), 50);
		this._comboCounter.add(() => HomeScreens.on('bottomLeft'), 1800);
		this._comboCounter.add(() => HomeScreens.on('bottomRight'), 2000);
		this._comboCounter.add(() => HomeScreens.off('bottomRight'), 3000);
		this._comboCounter.add(() => HomeScreens.on('bottomRight'), 3700);
		this._comboCounter.add(() => HomeScreens.off('topLeft'), 3800);
		this._comboCounter.add(() => HomeScreens.off('bottomLeft'), 5500);
	}

	private _doSwirl(): void {
		const sceneconfig = [
			{ rscale: 0.16, scale: 0.2, x: 0.0143 + 0.0445, y: 0.31, t: 0 },
			{ rscale: 1, scale: 1, x: -0.04 + 0.034, y: 0.5, t: 1200 },
			{ rscale: 1.05, scale: 1.1, x: -0.04 + 0.034, y: 0.5, t: 3300 },
			{ rscale: 3, scale: 0, x: -0.04 + 0.034, y: 0.5, t: 6000 }
		];

		let perRing = 360;
		let step = 4;

		let settings = [
			{ r: 495, s: 1.2, c: '#461567' },
			{ r: 465, s: 1.2, c: '#461567' },
			{ r: 435, s: 1.1, c: '#461567' },
			{ r: 410, s: 1, c: '#461567' },
			{ r: 390, s: 0.95, c: '#461567' },
			{ r: 370, s: 0.85, c: '#461567' },
			{ r: 350, s: 0.8, c: '#461567' },
			{ r: 330, s: 0.8, c: '#461567' },
			{ r: 310, s: 0.75, c: '#461567' },
			{ r: 290, s: 0.6, c: '#461567' },
			{ r: 270, s: 0.5, c: '#461567' },
			{ r: 250, s: 0.5, c: '#461567' },
			{ r: 230, s: 0.48, c: '#461567' },
			{ r: 210, s: 0.45, c: '#461567' },
			{ r: 190, s: 0.42, c: '#461567' },
			{ r: 170, s: 0.4, c: '#461567' },
			{ r: 150, s: 0.35, c: '#461567' },
			{ r: 130, s: 0.32, c: '#461567' },
			{ r: 110, s: 0.28, c: '#461567' },
			{ r: 90, s: 0.24, c: '#461567' },
			{ r: 70, s: 0.14, c: '#461567' },
			{ r: 50, s: 0.12, c: '#461567' },
			{ r: 30, s: 0.11, c: '#461567' },
			{ r: 10, s: 0.1, c: '#461567' }
		];

		var index = -1;

		let colours = HomeColours.graduated;

		if (globals.colourPhase === 'yellow') colours = colours.reverse();

		for (var i = 0; i < settings.length; i++) {
			let c = colours[i];
			let shape = 'solid2.png';
			let ran = Math.random();
			if (ran < 0.05) {
				shape = 'solid-square.png';
			} else if (ran < 0.4) {
				shape = 'solid-tri.png';
			}

			for (var j = 0; j < perRing; j += step) {
				index++;
				if (this._shapeParticles[index]) {
					this._shapeParticles[index].screens.swirl(settings[i], j, c, shape, sceneconfig);
				}
			}
		}

		HomeScreens.allOff();
		this._comboCounter.add(() => HomeScreens.on('topRight'), 50);
		this._comboCounter.add(() => HomeScreens.on('topLeft'), 1200);
		this._comboCounter.add(() => HomeScreens.on('bottomRight'), 1300);
		this._comboCounter.add(() => HomeScreens.on('bottomLeft'), 1800);
		this._comboCounter.add(() => HomeScreens.allOff(), 7000);

		for (i = index - 1; i < this._shapeParticles.length - 1; i++) {
			this._shapeParticles[i].deactivate();
		}
	}




	private _enableForHome(): void {
		this._active = true;
		this._screensParticleContainer.visible = true;

		this._startState();

		this._screensPool.forEach((p: HomeParticle) => {
			p.active = true;
		});
	}
}
